import moment from 'moment-timezone';
import { AbstractModel } from '../base';
import { CustomerPricing, ICustomerPricing } from '../customerpricing';
import { IDocument, Document, IDocumentTag, DocumentTag } from '../document';
import {
  QuoteClassificationEnum,
  QuoteProjectSizeEnum,
  QuoteSurchargeApplicationTypeEnum
} from './quote.constants';
import {
  IQuote,
  IQuoteAddress,
  IQuoteAddressData,
  IQuoteContact,
  IQuoteContactData,
  IQuoteCustomer,
  IQuoteCustomerData,
  IQuoteData,
  IQuoteExtraItem,
  IQuoteExtraItemData,
  IQuoteFreightItem,
  IQuoteFreightItemData,
  IQuotePaymentTermItem,
  IQuotePaymentTermItemData,
  IQuoteProductCategory,
  IQuoteProductCategoryData,
  IQuoteProductItem,
  IQuoteProductItemData,
  IQuoteServiceItem,
  IQuoteServiceItemData,
  IQuoteTermItem,
  IQuoteTermItemData,
  QuoteState,
  IQuoteFieldError,
  IQuoteFieldErrorData,
  IQuoteAddressFieldError,
  IQuoteAddressFieldErrorData,
  IQuoteContactFieldError,
  IQuoteContactFieldErrorData,
  IQuoteCustomerFieldError,
  IQuoteCustomerFieldErrorData,
  IQuoteVentilationRoom,
  IQuoteVentilationRoomData,
  IQuoteVentilationCapacity,
  IQuoteVentilationCapacityData,
  IQuoteVentilationCapacityFieldError,
  IQuoteVentilationCapacityFieldErrorData,
  IQuoteVentilationRoomFieldError,
  IQuoteVentilationRoomFieldErrorData,
  IQuoteCustomerPricing,
  IQuoteCustomerPricingData,
  QuoteOrganizationEnum,
  IQuoteOrganization,
  IQuoteFreightEstimates,
  IQuoteFreightEstimatesData,
  QuoteType,
  IQuoteProjectSize,
  IQuoteProjectSizeData,
  IQuoteClassification,
  IQuoteProductDocument,
  IQuoteDocument,
  IQuoteProductDocumentData,
  IQuoteDocumentData,
  IQuotePackage,
  IQuotePackageData,
  IQuotePackageDocument,
  IQuotePackageDocumentData,
  IQuoteDocumentTagData,
  IQuoteDocumentTag
} from './quote.types';

import { v4 as uuidv4 } from 'uuid';
import { Country, ICountry } from '../country';

export class QuotePackage extends AbstractModel implements IQuotePackage {
  readonly id?: number;
  readonly package_documents: IQuotePackageDocument[] = [];
  readonly quotes: IQuote[] = [];
  readonly created_at?: Date;
  readonly updated_at?: Date;

  constructor(data?: IQuotePackageData) {
    super();

    this.validate(data, []);

    if (data) {
      if (data.id) {
        this.id = data.id;
      }

      if (data.package_documents) {
        this.package_documents = data.package_documents.map(
          document => new QuotePackageDocument(document)
        );
      }

      if (data.quotes) {
        this.quotes = data.quotes.map(quote => new Quote(quote));
      }

      if (data.created_at) {
        this.created_at =
          typeof data.created_at === 'string'
            ? new Date(data.created_at)
            : data.created_at;
      }

      if (data.updated_at) {
        this.updated_at =
          typeof data.updated_at === 'string'
            ? new Date(data.updated_at)
            : data.updated_at;
      }
    }
  }
}

export class QuotePackageDocument extends AbstractModel
  implements IQuotePackageDocument {
  readonly id?: number;
  readonly quote_document: number | IQuoteDocument;
  readonly quote_package?: number;
  readonly order: number;
  readonly section: number;

  constructor(data: IQuotePackageDocumentData) {
    super();

    const requiredFields: (keyof IQuotePackageDocumentData)[] = [
      'quote_document',
      'order',
      'section'
    ];
    this.validate(data, requiredFields);

    if (data.id) {
      this.id = data.id;
    }

    this.quote_document =
      typeof data.quote_document == 'number'
        ? data.quote_document
        : new QuoteDocument(data.quote_document!);

    if (data.quote_package) {
      this.quote_package = data.quote_package;
    }
    this.order = data.order!;
    this.section = data.section!;
  }
}

export class QuoteProductDocument extends AbstractModel
  implements IQuoteProductDocument {
  readonly id?: number;
  readonly quote_document: IQuoteDocument;

  constructor(data: IQuoteProductDocumentData) {
    super();

    const requiredFields: (keyof IQuoteProductDocumentData)[] = [
      'quote_document'
    ];
    this.validate(data, requiredFields);

    if (data.id) {
      this.id = data.id;
    }

    this.quote_document = new QuoteDocument(data.quote_document!);
  }

  toString(): string {
    return this.quote_document.document.toString();
  }
}

export class QuoteDocument extends AbstractModel implements IQuoteDocument {
  readonly id?: number;
  readonly document: IDocument;

  constructor(data: IQuoteDocumentData) {
    super();

    const requiredFields: (keyof IQuoteDocumentData)[] = ['document'];
    this.validate(data, requiredFields);

    if (data.id) {
      this.id = data.id;
    }

    this.document = new Document(data.document!);
  }

  toString(): string {
    return this.document.toString();
  }
}

export class QuoteDocumentTag extends AbstractModel
  implements IQuoteDocumentTag {
  readonly id?: number;
  readonly tag: IDocumentTag;

  constructor(data: IQuoteDocumentTagData) {
    super();

    const requiredFields: (keyof IQuoteDocumentTagData)[] = ['tag'];
    this.validate(data, requiredFields);

    if (data.id) {
      this.id = data.id;
    }

    this.tag = new DocumentTag(data.tag!);
  }

  toString(): string {
    return this.tag.toString();
  }
}

export class QuoteServiceItem implements IQuoteServiceItem {
  readonly id?: number;
  readonly title: string = '';
  readonly description: string = '';
  readonly price: number = 0;
  readonly days?: number;
  readonly template = null as number | null;
  readonly price_computation = null as number | null;
  readonly modified: boolean = false;

  constructor(data?: IQuoteServiceItemData) {
    if (data) {
      if (data.id) {
        this.id = data.id;
      }
      if (data.days) {
        this.days = Number(data.days);
      }

      this.modified = data.modified ?? false;
      this.title = data.title;
      this.description = data.description;
      this.price = Number(data.price);
      this.template = data.template;
      this.price_computation = data.price_computation ?? null;
    }
  }

  validate(): boolean {
    throw new Error('Method not implemented.');
  }
}

export class QuoteFreightEstimates implements IQuoteFreightEstimates {
  readonly twenty_feet_containers_quantity: number = 0;
  readonly forty_feet_containers_quantity: number = 0;
  readonly fifty_three_feet_trucks_quantity: number = 0;
  readonly forty_feet_containers_hc_quantity: number = 0;
  readonly flat_rack_quantity: number = 0;
  readonly flat_bed_trucks_quantity: number = 0;
  readonly conestoga_trucks_quantity: number = 0;

  constructor(data?: IQuoteFreightEstimatesData) {
    if (data) {
      const validatedFields: (keyof IQuoteFreightEstimatesData)[] = [
        'twenty_feet_containers_quantity',
        'forty_feet_containers_quantity',
        'fifty_three_feet_trucks_quantity',
        'forty_feet_containers_hc_quantity',
        'flat_rack_quantity',
        'flat_bed_trucks_quantity',
        'conestoga_trucks_quantity'
      ];

      this.validate(data, validatedFields);

      if (data.twenty_feet_containers_quantity) {
        this.twenty_feet_containers_quantity =
          data.twenty_feet_containers_quantity;
      }

      if (data.forty_feet_containers_quantity) {
        this.forty_feet_containers_quantity =
          data.forty_feet_containers_quantity;
      }

      if (data.fifty_three_feet_trucks_quantity) {
        this.fifty_three_feet_trucks_quantity =
          data.fifty_three_feet_trucks_quantity;
      }

      if (data.forty_feet_containers_hc_quantity) {
        this.forty_feet_containers_hc_quantity =
          data.forty_feet_containers_hc_quantity;
      }

      if (data.flat_rack_quantity) {
        this.flat_rack_quantity = data.flat_rack_quantity;
      }

      if (data.flat_bed_trucks_quantity) {
        this.flat_bed_trucks_quantity = data.flat_bed_trucks_quantity;
      }

      if (data.conestoga_trucks_quantity) {
        this.conestoga_trucks_quantity = data.conestoga_trucks_quantity;
      }
    }
  }

  validate(
    data: IQuoteFreightEstimatesData,
    validatedFields: (keyof IQuoteFreightEstimatesData)[]
  ) {
    for (const field of validatedFields) {
      if (data[field]! < 0) {
        throw new Error(`Invalid '${field.toString()}' property`);
      }
    }
  }
}

export class QuoteFreightItem implements IQuoteFreightItem {
  readonly id?: number;
  readonly title: string = '';
  readonly description: string = '';
  readonly location: string = '';
  readonly price: number = 0;
  readonly template = null as number | null;

  readonly estimates: IQuoteFreightEstimates | null = null;

  constructor(data?: IQuoteFreightItemData) {
    if (data) {
      if (data.id) {
        this.id = data.id;
      }

      if (data.location) {
        this.location = data.location;
      }

      if (data.estimates) {
        this.estimates = new QuoteFreightEstimates(data.estimates);
      }

      this.title = data.title;
      this.description = data.description;
      this.price = Number(data.price);
      this.template = data.template;
    }
  }

  validate(): boolean {
    throw new Error('Method not implemented.');
  }
}

export class QuoteExtraItem implements IQuoteExtraItem {
  readonly id?: number;
  readonly title: string = '';
  readonly description: string = '';
  readonly price: number = 0;

  constructor(data?: IQuoteExtraItemData) {
    if (data) {
      if (data.id) {
        this.id = data.id;
      }

      this.title = data.title;
      this.description = data.description;
      this.price = Number(data.price);
    }
  }

  validate(): boolean {
    throw new Error('Method not implemented.');
  }
}

export class QuoteTermItem implements IQuoteTermItem {
  readonly id?: number;
  readonly title: string = '';
  readonly description: string = '';
  readonly template: null | number = null;
  readonly order: number = 1;

  constructor(data?: IQuoteTermItemData) {
    if (data) {
      if (data.id) {
        this.id = data.id;
      }

      this.title = data.title;
      this.description = data.description;
      this.template = data.template;
      this.order = data.order;
    }
  }

  validate(): boolean {
    throw new Error('Method not implemented.');
  }
}

export class QuotePaymentTermItem implements IQuotePaymentTermItem {
  readonly id?: number;
  readonly title: string = '';
  readonly description: string = '';
  readonly template = null as number | null;

  constructor(data?: IQuotePaymentTermItemData) {
    if (data) {
      if (data.id) {
        this.id = data.id;
      }

      this.title = data.title;
      this.description = data.description;
      this.template = data.template;
    }
  }

  validate(): boolean {
    throw new Error('Method not implemented.');
  }
}

export class QuoteProductItem implements IQuoteProductItem {
  readonly id?: number;
  readonly module?: number;
  readonly description: string = '';
  readonly configuration?: number;
  readonly price: number = 0;
  readonly quantity: number = 1;

  /**
   * Used to identify current quote product item instead of id in cases of
   * editing/creating before sending that data to the server.
   *
   * ## Reason ##
   * When creating duplicates of a product item no `id` is assigned and finding the correct product's index
   * cannot be done by id. `uuid` allows the product to use a proxy id in case to identify the correct
   * product and it's index.
   */
  readonly uuid: string = uuidv4();

  constructor(data?: IQuoteProductItemData) {
    if (data) {
      if (data.id) {
        this.id = data.id;
      }

      if (data.module) {
        this.module = data.module;
      }

      if (data.description) {
        this.description = data.description;
      }

      if (data.configuration) {
        this.configuration = data.configuration;
      }

      if (data.price) {
        this.price = data.price;
      }

      if (data.quantity) {
        this.quantity = data.quantity;
      }
    }
  }

  validate(): boolean {
    throw new Error('Method not implemented.');
  }

  isEqual(product: IQuoteProductItem): boolean {
    let isSame = false;
    if (this.id && product.id) {
      isSame = this.id == product.id;
    } else {
      isSame = this.uuid == (product as QuoteProductItem).uuid;
    }
    return isSame;
  }
}

export class QuoteProductCategory implements IQuoteProductCategory {
  readonly id?: number;
  readonly module: number;
  readonly price: number = 0;
  readonly discount: number = 0;
  readonly global_price: number = 0;
  readonly global_discount: number = 0;
  readonly accessory_price: number = 0;
  readonly accessory_discount: number = 0;
  readonly global_discount_percentage: number = 0;
  readonly accessory_discount_percentage: number = 0;
  first_year_subscription_discount_percentage = 0;
  readonly first_year_subscription_discount: number = 0;
  readonly effective_accessory_discount_percentage: number = 0;
  readonly effective_core_discount_percentage: number = 0;
  readonly products: IQuoteProductItem[] = [];

  constructor(data: IQuoteProductCategoryData) {
    if (data.id) {
      this.id = data.id;
    }

    this.module = data.module;

    if (data.price) {
      this.price = data.price;
    }

    if (data.discount) {
      this.discount = data.discount;
    }

    if (data.global_price) {
      this.global_price = data.global_price;
    }

    if (data.global_discount) {
      this.global_discount = data.global_discount;
    }

    if (data.global_discount_percentage) {
      this.global_discount_percentage = data.global_discount_percentage;
    }

    if (data.accessory_price) {
      this.accessory_price = data.accessory_price;
    }

    if (data.accessory_discount) {
      this.accessory_discount = data.accessory_discount;
    }

    if (data.accessory_discount_percentage) {
      this.accessory_discount_percentage = data.accessory_discount_percentage;
    }

    if (data.effective_accessory_discount_percentage) {
      this.effective_accessory_discount_percentage =
        data.effective_accessory_discount_percentage;
    }

    if (data.effective_core_discount_percentage) {
      this.effective_core_discount_percentage =
        data.effective_core_discount_percentage;
    }

    if (data.first_year_subscription_discount_percentage) {
      this.first_year_subscription_discount_percentage =
        data.first_year_subscription_discount_percentage;
    }

    if (data.first_year_subscription_discount) {
      this.first_year_subscription_discount =
        data.first_year_subscription_discount;
    }

    if (data.products) {
      this.products = data.products.map(
        product => new QuoteProductItem(product)
      );
    }
  }

  validate(): boolean {
    throw new Error('Method not implemented.');
  }

  addProduct(product: IQuoteProductItem): void {
    this.products.push(Object.assign({}, product));
  }

  updateProduct(product: IQuoteProductItem): void {
    const index = this.products.findIndex(
      productItem => productItem.id == product.id
    );

    if (index != -1) {
      this.products.splice(index, 1, product);
    }
    // TODO: We should really be doing a return that let's
    // caller knows if update was successful
  }

  removeProduct(product: IQuoteProductItem): void {
    const index = this.products.findIndex(
      productItem => productItem.id == product.id
    );

    if (index != -1) {
      this.products.splice(index, 1);
    }
    // TODO: We should really be doing a return that let's
    // caller knows if remove was successful
  }
}

export class QuoteAddress implements IQuoteAddress {
  readonly id?: number;
  readonly address1: string = '';
  readonly address2: string = '';
  readonly address3: string = '';
  readonly address4: string = '';
  readonly city: string = '';
  readonly region: string = '';
  readonly postal_code: string = '';
  readonly country: string = '';
  readonly country_code: ICountry | string = '';

  constructor(data?: IQuoteAddressData) {
    if (data) {
      if (data.id) {
        this.id = data.id;
      }

      if (data.address1) {
        this.address1 = data.address1;
      }

      if (data.address2) {
        this.address2 = data.address2;
      }

      if (data.address3) {
        this.address3 = data.address3;
      }

      if (data.address4) {
        this.address4 = data.address4;
      }

      if (data.city) {
        this.city = data.city;
      }

      if (data.region) {
        this.region = data.region;
      }

      if (data.country) {
        this.country = data.country;
      }

      if (data.country_code) {
        this.country_code =
          typeof data.country_code === 'string'
            ? data.country_code
            : new Country(data.country_code);
      }

      if (data.postal_code) {
        this.postal_code = data.postal_code;
      }
    }
  }

  validate(): boolean {
    throw new Error('Method not implemented.');
  }
}

export class QuoteCustomerPricing implements IQuoteCustomerPricing {
  readonly snapshot?: number;
  readonly validity?: string;

  constructor(data?: IQuoteCustomerPricingData) {
    if (data) {
      if (data.snapshot) {
        this.snapshot = data.snapshot;
      }

      if (data.validity) {
        this.validity = data.validity;
      }
    }
  }
  validate(): boolean {
    throw Error('validate not implmented yet');
  }
}
export class QuoteContact implements IQuoteContact {
  readonly id?: number;
  readonly first_name: string = '';
  readonly last_name: string = '';
  readonly email: string = '';
  readonly phone: string = '';

  constructor(data?: IQuoteContactData) {
    if (data) {
      if (data.id) {
        this.id = data.id;
      }

      if (data.first_name) {
        this.first_name = data.first_name;
      }

      if (data.last_name) {
        this.last_name = data.last_name;
      }

      if (data.email) {
        this.email = data.email;
      }

      if (data.phone) {
        this.phone = data.phone;
      }
    }
  }

  validate(): boolean {
    throw new Error('Method not implemented.');
  }
}

export class QuoteCustomer implements IQuoteCustomer {
  readonly id?: number;
  readonly name: string = '';
  readonly identifier: string = '';
  readonly tax_id: string = '';
  readonly address: QuoteAddress = new QuoteAddress();
  readonly ship_to_name: string = '';
  readonly ship_address: QuoteAddress = new QuoteAddress();
  readonly contact: IQuoteContact = new QuoteContact();
  readonly is_same_billing_address: boolean = true;
  readonly contract: null | ICustomerPricing = null;

  constructor(data?: IQuoteCustomerData) {
    if (data) {
      if (data.id) {
        this.id = data.id;
      }

      if (data.name) {
        this.name = data.name;
      }

      if (data.identifier) {
        this.identifier = data.identifier;
      }

      if (data.tax_id) {
        this.tax_id = data.tax_id;
      }

      this.is_same_billing_address = data.is_same_billing_address ?? true;

      if (data.address) {
        this.address = new QuoteAddress(data.address);
      }

      if (data.ship_to_name) {
        this.ship_to_name = data.ship_to_name;
      }

      if (data.ship_address) {
        this.ship_address = new QuoteAddress(data.ship_address);
      }

      if (data.contact) {
        this.contact = new QuoteContact(data.contact);
      }
      if (data.contract) {
        this.contract = new CustomerPricing(data.contract);
      }
    }
  }
}

export class QuoteVentilationCapacity implements IQuoteVentilationCapacity {
  quote?: number;
  incubation_chiller_capacities_kw: number | null = null;
  ventilation_chiller_capacities_kw: number | null = null;
  incubation_boiler_capacities_kw: number | null = null;
  ventilation_boiler_capacities_kw: number | null = null;
  summer_temp_c: number | null = null;
  summer_temp_f: number | null = null;
  winter_temp_c: number | null = null;
  winter_temp_f: number | null = null;
  summer_rh: number | null = null;
  winter_rh: number | null = null;

  voltage_phase_frequency?: string = '';
  boiler_type?: string = '';

  ventilation_chiller_capacities_ton: number | null = null;
  incubation_chiller_capacities_ton: number | null = null;
  ventilation_boiler_capacities_mbh: number | null = null;
  incubation_boiler_capacities_mbh: number | null = null;

  constructor(data?: IQuoteVentilationCapacityData) {
    if (data) {
      if (data.quote) {
        this.quote = data.quote;
      }

      this.incubation_chiller_capacities_kw =
        data.incubation_chiller_capacities_kw ?? null;
      this.ventilation_chiller_capacities_kw =
        data.ventilation_chiller_capacities_kw ?? null;
      this.incubation_boiler_capacities_kw =
        data.incubation_boiler_capacities_kw ?? null;
      this.ventilation_boiler_capacities_kw =
        data.ventilation_boiler_capacities_kw;
      this.summer_temp_c = data.summer_temp_c ?? null;
      this.summer_temp_f = data.summer_temp_f ?? null;
      this.winter_temp_c = data.winter_temp_c ?? null;
      this.winter_temp_f = data.winter_temp_f ?? null;
      this.summer_rh = data.summer_rh ?? null;
      this.winter_rh = data.winter_rh ?? null;

      this.voltage_phase_frequency = data.voltage_phase_frequency ?? '';
      this.boiler_type = data.boiler_type ?? '';

      this.ventilation_chiller_capacities_ton =
        data.ventilation_chiller_capacities_ton ?? null;
      this.incubation_chiller_capacities_ton =
        data.incubation_chiller_capacities_ton ?? null;
      this.ventilation_boiler_capacities_mbh =
        data.ventilation_boiler_capacities_mbh ?? null;
      this.incubation_boiler_capacities_mbh =
        data.incubation_boiler_capacities_mbh ?? null;
    }
  }

  validate(): boolean {
    throw new Error('Method not implemented.');
  }
}

export class QuoteVentilationRoom implements IQuoteVentilationRoom {
  id?: number;
  quote: number;
  description?: string;

  constructor(data: IQuoteVentilationRoomData) {
    if (data.id) {
      this.id = data.id;
    }
    this.quote = data.quote;
    this.description = data.description ?? '';
  }

  validate(): boolean {
    throw new Error('Method not implemented.');
  }
}
export class Quote implements IQuote {
  id?: number;
  number?: number;
  revision = 0;
  owner: string | number = '';
  language = 'en';
  state: QuoteState = QuoteState.DRAFT;
  type?: number;
  subtype = 0;
  layout = '';
  project = '';
  validity_date_range = 30;
  exchange_rate? = 1;
  currency?: number;
  amount = 0;
  latest = true;
  agent?: string | number;
  inside_sales_owner?: string | number;
  comments = '';
  notes = '';
  ewg_discount = 0;
  ewg_discount_percent = 0;
  special_discount = 0;
  special_discount_percent = 0;
  commission = 0;
  commission_percent = 0;

  effective_blended_discount_percentage = 0;
  target_discount_percent = 0;

  effective_accessory_discount_percentage = 0;
  effective_core_discount_percentage = 0;

  effective_equipment_discount_percentage = 0;

  region?: number;

  readonly equipment_nettotal?: number = 0;
  readonly extras_subtotal?: number = 0;
  readonly freight_subtotal?: number = 0;
  readonly services_subtotal?: number = 0;

  equipment_subtotal = 0;
  subscription_first_year_discount = 0;
  subscription_yearly_subtotal = 0;
  readonly surcharge: number = 0;
  readonly surcharge_discount_percent: number = 0;
  readonly net_surcharge: number = 0;
  readonly surcharge_discount: number = 0;
  readonly surcharge_application_type: QuoteSurchargeApplicationTypeEnum =
    QuoteSurchargeApplicationTypeEnum.None;

  is_test = false;

  // should be mandatory
  rfq_date: Date | string | null = null;
  rfq_number: string | null = null;
  rfq_attachment: string | null = null;

  customer: IQuoteCustomer = new QuoteCustomer();
  categories: IQuoteProductCategory[] = [];
  service_items: IQuoteServiceItem[] = [];
  freight_items: IQuoteFreightItem[] = [];
  extra_items: IQuoteExtraItem[] = [];
  terms: IQuoteTermItem[] = [];
  payment_terms: IQuotePaymentTermItem[] = [];

  //ventilations
  capacity: IQuoteVentilationCapacity | null = null;
  rooms: IQuoteVentilationRoom[] = [];

  organization: QuoteOrganizationEnum = QuoteOrganizationEnum.Jamesway;

  customer_relationship_owner = '';
  bidding_environment = '';
  classification: null | number = null;

  project_size?: IQuoteProjectSize;

  is_budgetary = false;

  readonly salesforce_opportunity_id: string | null = null;

  readonly validity_start_date?: Date | string;

  readonly highest_approval_level: QuoteState = QuoteState.APPROVAL_4_PENDING;

  readonly created_at?: Date | string;
  readonly updated_at?: Date | string;

  readonly package: number | null = null;

  constructor(data?: IQuoteData) {
    if (data) {
      if (data.id) {
        this.id = data.id;
      }

      this.type = data.type ?? undefined;

      this.subtype = data.subtype ?? 0;

      this.number = data.number ?? undefined;

      this.revision = data.revision ?? 0;

      this.owner = data.owner ?? '';

      this.language = data.language ?? 'en';

      this.state = data.state ?? QuoteState.DRAFT;

      this.layout = data.layout ?? '';

      this.project = data.project ?? '';

      if (data.validity_date_range) {
        this.validity_date_range = data.validity_date_range;
      }

      this.exchange_rate = Number(data.exchange_rate ?? 1);

      this.currency = data.currency ?? undefined;

      this.amount = data.amount ?? 0;

      this.latest = data.latest ?? true;

      this.agent = data.agent ?? undefined;

      this.inside_sales_owner = data.inside_sales_owner ?? undefined;

      this.region = data.region ?? undefined;

      this.comments = data.comments ?? '';

      this.notes = data.notes ?? '';

      this.ewg_discount = data.ewg_discount ?? 0;

      this.ewg_discount_percent = data.ewg_discount_percent ?? 0;

      this.special_discount = data.special_discount ?? 0;

      this.special_discount_percent = data.special_discount_percent ?? 0;

      this.equipment_subtotal = data.equipment_subtotal ?? 0;

      this.subscription_first_year_discount =
        data.subscription_first_year_discount ?? 0;

      this.subscription_yearly_subtotal =
        data.subscription_yearly_subtotal ?? 0;

      if (data.surcharge) {
        this.surcharge = data.surcharge;
      }

      if (data.surcharge_discount_percent) {
        this.surcharge_discount_percent = data.surcharge_discount_percent;
      }

      if (data.net_surcharge) {
        this.net_surcharge = data.net_surcharge;
      }

      if (data.surcharge_discount) {
        this.surcharge_discount = data.surcharge_discount;
      }

      if (data.surcharge_application_type) {
        this.surcharge_application_type = data.surcharge_application_type;
      }

      this.commission = data.commission ?? 0;

      this.commission_percent = data.commission_percent ?? 0;

      this.target_discount_percent = data.target_discount_percent ?? 0;
      this.effective_blended_discount_percentage =
        data.effective_blended_discount_percentage ?? 0;

      this.effective_accessory_discount_percentage =
        data.effective_accessory_discount_percentage ?? 0;
      this.effective_core_discount_percentage =
        data.effective_core_discount_percentage ?? 0;

      this.effective_equipment_discount_percentage =
        data.effective_equipment_discount_percentage ?? 0;

      this.customer = new QuoteCustomer(data.customer);

      this.rfq_date = data.rfq_date ?? null;

      if (data.rfq_number) {
        this.rfq_number = data.rfq_number;
      }

      this.rfq_attachment = data.rfq_attachment ?? null;

      if (data.is_budgetary) {
        this.is_budgetary = data.is_budgetary;
      }

      this.categories =
        data.categories?.map(category => new QuoteProductCategory(category)) ??
        [];

      this.service_items =
        data.service_items?.map(service => new QuoteServiceItem(service)) ?? [];

      this.freight_items =
        data.freight_items?.map(freight => new QuoteFreightItem(freight)) ?? [];

      this.extra_items =
        data.extra_items?.map(extra => new QuoteExtraItem(extra)) ?? [];

      this.terms = data.terms?.map(term => new QuoteTermItem(term)) ?? [];

      this.payment_terms =
        data.payment_terms?.map(term => new QuotePaymentTermItem(term)) ?? [];

      //ventilations
      this.rooms =
        data.rooms?.map(room => new QuoteVentilationRoom(room)) ?? [];

      this.capacity = data.capacity
        ? new QuoteVentilationCapacity(data.capacity)
        : null;

      this.validity_start_date = data.validity_start_date ?? undefined;

      // Branding
      this.organization = data.organization!;

      this.is_test = data.is_test ?? false;

      this.customer_relationship_owner = data.customer_relationship_owner ?? '';
      this.bidding_environment = data.bidding_environment ?? '';
      this.classification = data.classification ?? null;

      this.highest_approval_level = data.highest_approval_level!;

      if (data.project_size) {
        this.project_size = new QuoteProjectSize(data.project_size);
      }

      if (data.package) {
        this.package = data.package;
      }

      if (data.equipment_nettotal) {
        this.equipment_nettotal = data.equipment_nettotal;
      }
      if (data.extras_subtotal) {
        this.extras_subtotal = data.extras_subtotal;
      }
      if (data.freight_subtotal) {
        this.freight_subtotal = data.freight_subtotal;
      }
      if (data.services_subtotal) {
        this.services_subtotal = data.services_subtotal;
      }

      if (data.salesforce_opportunity_id) {
        this.salesforce_opportunity_id = data.salesforce_opportunity_id;
      }

      this.created_at = data.created_at ?? undefined;
      this.updated_at = data.updated_at ?? undefined;
    }
  }

  addVentilationRoom(room: IQuoteVentilationRoom): void {
    this.rooms.push(room);
  }

  updateVentilationRoom(room: IQuoteVentilationRoom): void {
    const index = this.rooms.findIndex(
      ventilationRoom => ventilationRoom.id == room.id
    );

    if (index != -1) {
      this.rooms.splice(index, 1, room);
    }
  }

  removeVentilationRoom(room: IQuoteVentilationRoom): void {
    const index = this.rooms.findIndex(
      ventilationRoom => ventilationRoom.id == room.id
    );

    if (index != -1) {
      this.rooms.splice(index, 1);
    }
  }

  addProductItem(product: IQuoteProductItem): void {
    const quoteProductCategory = this.categories.find(
      ({ module }) => module == product.module
    );

    if (quoteProductCategory) {
      quoteProductCategory.addProduct(product);
    } else {
      const newQuoteProductCategory = new QuoteProductCategory({
        module: product.module!
      });

      newQuoteProductCategory.addProduct(product);
      this.categories.push(newQuoteProductCategory);
    }
  }

  updateProductItem(product: IQuoteProductItem): void {
    const quoteProductCategory = this.categories.find(
      ({ module }) => module == product.module
    );

    if (quoteProductCategory) {
      quoteProductCategory.updateProduct(product);
    }
  }

  removeProductItem(product: IQuoteProductItem): void {
    const quoteProductCategory = this.categories.find(
      ({ module }) => module == product.module
    );

    if (quoteProductCategory) {
      quoteProductCategory.removeProduct(product);
    }
  }

  getAllProducts(): IQuoteProductItem[] {
    const productList: IQuoteProductItem[] = [];

    this.categories.map(quoteProductCategory => {
      quoteProductCategory.products?.map(product => {
        productList.push(product);
      });
    });

    return productList;
  }

  getOrganization(): IQuoteOrganization {
    let state = null;
    switch (this.organization) {
      case QuoteOrganizationEnum.Jamesway_SEA:
        state = QuoteOrganization.JameswaySEA;
        break;

      case QuoteOrganizationEnum.Jamesway:
        state = QuoteOrganization.Jamesway;
        break;

      default:
        throw new Error('Unexpected organization.');
    }
    return state;
  }

  getClassification(): IQuoteClassification | null {
    let classification = null;
    switch (this.classification) {
      case QuoteClassificationEnum.CLASS_1:
        classification = QuoteClassification.CLASS_1;
        break;

      case QuoteClassificationEnum.CLASS_2:
        classification = QuoteClassification.CLASS_2;
        break;

      case QuoteClassificationEnum.CLASS_3:
        classification = QuoteClassification.CLASS_3;
        break;

      default:
        // keep classification null
        break;
    }

    return classification;
  }

  isMachineQuote(): boolean {
    return this.type === QuoteType.MACHINE_PROJECTS;
  }

  isAutomationQuote(): boolean {
    return this.type === QuoteType.AUTOMATION;
  }

  isVentilationQuote(): boolean {
    return (
      this.type === QuoteType.RTU_VENTILATION ||
      this.type === QuoteType.AIRWAY_VENTILATION
    );
  }

  isExpired(): boolean {
    return moment(this.expiry_date).isBefore(new Date());
  }

  get expiry_date(): Date {
    return moment(this.validity_start_date)
      .add(this.validity_date_range, 'd')
      .toDate();
  }

  validate(): boolean {
    //TODO: Add Validators
    throw new Error('Method not implemented.');
  }

  isQuoteVentilation(): boolean {
    return (
      this.type == QuoteType.RTU_VENTILATION ||
      this.type == QuoteType.AIRWAY_VENTILATION
    );
  }

  highestApprovalRole(): string {
    switch (this.highest_approval_level) {
      case QuoteState.REVIEWING_DISCOUNTS:
      case QuoteState.IN_REVIEW:
        return 'Order Management';

      case QuoteState.APPROVAL_2_PENDING:
        return 'Sales Director';

      case QuoteState.APPROVAL_3_PENDING:
        return 'CCO';

      case QuoteState.APPROVAL_4_PENDING:
      default: {
        let approval_level = 'CEO';
        if (this.isQuoteVentilation()) {
          approval_level += ' or CCO';
        }
        return approval_level;
      }
    }
  }

  canCreateSalesOpportunity(): boolean {
    const VALID_STATES = [
      QuoteState.OUT_TO_CUSTOMER,
      QuoteState.AWARDED,
      QuoteState.LOST,
      QuoteState.ORDER_CREATED
    ];

    return (
      VALID_STATES.includes(this.state) &&
      !!this.rfq_number &&
      !this.salesforce_opportunity_id
    );
  }

  isTariffsApplicable(): boolean {
    if (!this.customer.ship_address) {
      return false;
    }
    // Maps the string representation of the country_code
    return (this.customer.ship_address.country_code as ICountry).code === 'US';
  }

  toString() {
    let str = `${this.number}`;

    if (this.revision > 0) {
      str = str + `R${this.revision}`;
    }

    return str;
  }

  toCustomerProposalString() {
    let str = `${this.toString()}`;

    if (this.customer) {
      str += ` - ${this.customer.name}`;
    }

    return str;
  }
}

export class QuoteProjectSize implements IQuoteProjectSize {
  readonly value: QuoteProjectSizeEnum;
  readonly type: string;

  constructor(data: IQuoteProjectSizeData) {
    this.value = data.value;
    this.type = data.type;
  }

  toString() {
    return this.type;
  }
}

export class QuoteClassification implements IQuoteClassification {
  readonly value: QuoteClassificationEnum;
  readonly display_name: string;

  constructor(data: IQuoteClassification) {
    this.value = data.value;
    this.display_name = data.display_name;
  }

  public toString(): string {
    return `${this.value} - ${this.display_name}`;
  }

  static CLASS_1 = new QuoteClassification({
    value: QuoteClassificationEnum.CLASS_1,
    display_name: 'Assertive'
  });

  static CLASS_2 = new QuoteClassification({
    value: QuoteClassificationEnum.CLASS_2,
    display_name: 'Effective'
  });

  static CLASS_3 = new QuoteClassification({
    value: QuoteClassificationEnum.CLASS_3,
    display_name: 'Passive'
  });

  static getClassificationList(): QuoteClassification[] {
    return [
      QuoteClassification.CLASS_1,
      QuoteClassification.CLASS_2,
      QuoteClassification.CLASS_3
    ];
  }
}

export class QuoteOrganization {
  readonly value: QuoteOrganizationEnum;
  readonly display_name: string;

  constructor(data: IQuoteOrganization) {
    this.value = data.value;
    this.display_name = data.display_name;
  }

  public toString(): string {
    return this.display_name;
  }

  static JameswaySEA = new QuoteOrganization({
    value: QuoteOrganizationEnum.Jamesway_SEA,
    display_name: 'Jamesway Chick Master Southeast Asia SDN. BHD.'
  });

  static Jamesway = new QuoteOrganization({
    value: QuoteOrganizationEnum.Jamesway,
    display_name: 'Jamesway Chick Master Incubator'
  });

  static getOrganizationList(): QuoteOrganization[] {
    return [QuoteOrganization.Jamesway, QuoteOrganization.JameswaySEA];
  }
}

export class QuoteFieldError implements IQuoteFieldError {
  number: string[] = [];
  revision: string[] = [];
  owner: string[] = [];
  language: string[] = [];
  state: string[] = [];
  type: string[] = [];
  subtype: string[] = [];
  layout: string[] = [];
  project: string[] = [];
  validity_date_range: string[] = [];
  exchange_rate: string[] = [];
  currency: string[] = [];
  latest: string[] = [];
  agent: string[] = [];
  inside_sales_owner: string[] = [];
  comments: string[] = [];
  notes: string[] = [];
  region: string[] = [];
  rfq_date: string[] = [];
  rfq_number: string[] = [];
  customer: IQuoteCustomerFieldError = new QuoteCustomerFieldError();
  capacity: IQuoteVentilationCapacityFieldError = new QuoteVentilationCapacityFieldError();
  rooms: IQuoteVentilationRoomFieldError[] = [];
  is_budgetary: string[] = [];

  constructor(data?: IQuoteFieldErrorData) {
    if (data) {
      this.number = data.number ?? [];
      this.revision = data.revision ?? [];
      this.owner = data.owner ?? [];
      this.language = data.language ?? [];
      this.state = data.state ?? [];
      this.type = data.type ?? [];
      this.subtype = data.subtype ?? [];
      this.layout = data.layout ?? [];
      this.project = data.project ?? [];
      this.validity_date_range = data.validity_date_range ?? [];
      this.exchange_rate = data.exchange_rate ?? [];
      this.currency = data.currency ?? [];
      this.latest = data.latest ?? [];
      this.agent = data.agent ?? [];
      this.inside_sales_owner = data.inside_sales_owner ?? [];
      this.comments = data.comments ?? [];
      this.notes = data.notes ?? [];
      this.region = data.region ?? [];
      this.rfq_date = data.rfq_date ?? [];
      this.rfq_number = data.rfq_number ?? [];
      this.customer = new QuoteCustomerFieldError(data.customer);
      this.capacity = new QuoteVentilationCapacityFieldError(data.capacity);

      this.rooms = data.rooms
        ? data.rooms.map(room => new QuoteVentilationRoomFieldError(room))
        : [];

      this.is_budgetary = data.is_budgetary ?? [];
    }
  }

  clearErrors(): void {
    this.number.splice(0, this.number.length);
    this.revision.splice(0, this.revision.length);
    this.owner.splice(0, this.owner.length);
    this.language.splice(0, this.language.length);
    this.state.splice(0, this.state.length);
    this.type.splice(0, this.type.length);
    this.subtype.splice(0, this.subtype.length);
    this.layout.splice(0, this.layout.length);
    this.project.splice(0, this.project.length);
    this.validity_date_range.splice(0, this.validity_date_range.length);
    this.exchange_rate.splice(0, this.exchange_rate.length);
    this.currency.splice(0, this.currency.length);
    this.latest.splice(0, this.latest.length);
    this.agent.splice(0, this.agent.length);
    this.inside_sales_owner.splice(0, this.inside_sales_owner.length);
    this.comments.splice(0, this.comments.length);
    this.notes.splice(0, this.notes.length);
    this.region.splice(0, this.region.length);
    this.rfq_date.splice(0, this.rfq_date.length);
    this.rfq_number.splice(0, this.rfq_number.length);

    // class data structures
    this.customer.clearErrors();
    this.capacity.clearErrors();
    this.rooms.splice(0, this.rooms.length);

    // categories.clearErrors()
    // service_items.clearErrors()
    // freight_items.clearErrors()
    // extra_items.clearErrors()
    // terms.clearErrors()
    // payment_terms.clearErrors()
  }

  static isQuoteFieldError(data: IQuoteFieldErrorData): boolean {
    if (data.number && Array.isArray(data.number)) {
      return true;
    }
    if (data.revision && Array.isArray(data.revision)) {
      return true;
    }
    if (data.owner && Array.isArray(data.owner)) {
      return true;
    }
    if (data.language && Array.isArray(data.language)) {
      return true;
    }
    if (data.state && Array.isArray(data.state)) {
      return true;
    }
    if (data.type && Array.isArray(data.type)) {
      return true;
    }
    if (data.subtype && Array.isArray(data.subtype)) {
      return true;
    }
    if (data.layout && Array.isArray(data.layout)) {
      return true;
    }
    if (data.project && Array.isArray(data.project)) {
      return true;
    }
    if (data.validity_date_range && Array.isArray(data.validity_date_range)) {
      return true;
    }
    if (data.exchange_rate && Array.isArray(data.exchange_rate)) {
      return true;
    }
    if (data.currency && Array.isArray(data.currency)) {
      return true;
    }
    if (data.latest && Array.isArray(data.latest)) {
      return true;
    }
    if (data.agent && Array.isArray(data.agent)) {
      return true;
    }
    if (data.inside_sales_owner && Array.isArray(data.inside_sales_owner)) {
      return true;
    }
    if (data.comments && Array.isArray(data.comments)) {
      return true;
    }
    if (data.notes && Array.isArray(data.notes)) {
      return true;
    }
    if (data.region && Array.isArray(data.region)) {
      return true;
    }

    if (data.rfq_date && Array.isArray(data.rfq_date)) {
      return true;
    }
    if (data.rfq_number && Array.isArray(data.rfq_number)) {
      return true;
    }
    // class data structures
    if (
      data.customer &&
      QuoteCustomerFieldError.isQuoteCustomerFieldError(data.customer)
    ) {
      return true;
    }
    if (
      data.capacity &&
      QuoteVentilationCapacityFieldError.isQuoteVentilationCapacityFieldError(
        data.capacity
      )
    ) {
      return true;
    }
    if (data.rooms && Array.isArray(data.rooms)) {
      return true;
    }

    return false;
  }
}

export class QuoteCustomerFieldError implements IQuoteCustomerFieldError {
  name: string[] = [];
  identifier: string[] = [];
  tax_id: string[] = [];
  is_same_billing_address: string[] = [];

  address: IQuoteAddressFieldError = new QuoteAddressFieldError();
  ship_to_name: string[] = [];
  ship_address: IQuoteAddressFieldError = new QuoteAddressFieldError();
  contact: IQuoteContactFieldError = new QuoteContactFieldError();

  constructor(data?: IQuoteCustomerFieldErrorData) {
    if (data) {
      this.name = data.name ?? [];
      this.identifier = data.identifier ?? [];
      this.tax_id = data.tax_id ?? [];
      this.is_same_billing_address = data.is_same_billing_address ?? [];

      this.contact = new QuoteContactFieldError(data.contact);
      this.ship_to_name = data.ship_to_name ?? [];
      this.ship_address = new QuoteAddressFieldError(data.ship_address);
      this.address = new QuoteAddressFieldError(data.address);
    }
  }

  clearErrors(): void {
    this.ship_to_name.splice(0, this.ship_to_name.length);
    this.name.splice(0, this.name.length);
    this.identifier.splice(0, this.identifier.length);
    this.tax_id.splice(0, this.tax_id.length);
    this.is_same_billing_address.splice(0, this.is_same_billing_address.length);

    // class data structure
    this.ship_address.clearErrors();
    this.address.clearErrors();
    this.contact.clearErrors();
  }

  static isQuoteCustomerFieldError(
    data: IQuoteCustomerFieldErrorData
  ): boolean {
    if (data.ship_to_name && Array.isArray(data.ship_to_name)) {
      return true;
    }
    if (data.name && Array.isArray(data.name)) {
      return true;
    }
    if (data.identifier && Array.isArray(data.identifier)) {
      return true;
    }
    if (data.tax_id && Array.isArray(data.tax_id)) {
      return true;
    }
    if (
      data.is_same_billing_address &&
      Array.isArray(data.is_same_billing_address)
    ) {
      return true;
    }
    // class data structure
    if (
      data.ship_address &&
      QuoteAddressFieldError.isQuoteAddressFieldError(data.ship_address)
    ) {
      return true;
    }
    if (
      data.address &&
      QuoteAddressFieldError.isQuoteAddressFieldError(data.address)
    ) {
      return true;
    }
    if (
      data.contact &&
      QuoteContactFieldError.isQuoteContactFieldError(data.contact)
    ) {
      return true;
    }

    return false;
  }
}

export class QuoteContactFieldError implements IQuoteContactFieldError {
  first_name: string[] = [];
  last_name: string[] = [];
  email: string[] = [];
  phone: string[] = [];

  constructor(data?: IQuoteContactFieldErrorData) {
    if (data) {
      this.first_name = data.first_name ?? [];
      this.last_name = data.last_name ?? [];
      this.email = data.email ?? [];
      this.phone = data.phone ?? [];
    }
  }

  clearErrors(): void {
    this.first_name.splice(0, this.first_name.length);
    this.last_name.splice(0, this.last_name.length);
    this.email.splice(0, this.email.length);
    this.phone.splice(0, this.phone.length);
  }

  static isQuoteContactFieldError(data: IQuoteContactFieldErrorData): boolean {
    if (data.first_name && Array.isArray(data.first_name)) {
      return true;
    }
    if (data.last_name && Array.isArray(data.last_name)) {
      return true;
    }
    if (data.email && Array.isArray(data.email)) {
      return true;
    }
    if (data.phone && Array.isArray(data.phone)) {
      return true;
    }
    return false;
  }
}

export class QuoteAddressFieldError implements IQuoteAddressFieldError {
  address1: string[] = [];
  address2: string[] = [];
  address3: string[] = [];
  address4: string[] = [];
  city: string[] = [];
  region: string[] = [];
  postal_code: string[] = [];
  country: string[] = [];
  country_code: string[] = [];

  constructor(data?: IQuoteAddressFieldErrorData) {
    if (data) {
      this.address1 = data.address1 ?? [];
      this.address2 = data.address2 ?? [];
      this.address3 = data.address3 ?? [];
      this.address4 = data.address4 ?? [];
      this.city = data.city ?? [];
      this.region = data.region ?? [];
      this.postal_code = data.postal_code ?? [];
      this.country = data.country ?? [];
      this.country_code = data.country_code ?? [];
    }
  }

  clearErrors(): void {
    this.address1.splice(0, this.address1.length);
    this.address2.splice(0, this.address2.length);
    this.address3.splice(0, this.address3.length);
    this.address4.splice(0, this.address4.length);
    this.city.splice(0, this.city.length);
    this.region.splice(0, this.region.length);
    this.postal_code.splice(0, this.postal_code.length);
    this.country.splice(0, this.country.length);
    this.country_code.splice(0, this.country_code.length);
  }

  static isQuoteAddressFieldError(data: IQuoteAddressFieldErrorData): boolean {
    if (data.address1 && Array.isArray(data.address1)) {
      return true;
    }
    if (data.address2 && Array.isArray(data.address2)) {
      return true;
    }
    if (data.address3 && Array.isArray(data.address3)) {
      return true;
    }
    if (data.address4 && Array.isArray(data.address4)) {
      return true;
    }
    if (data.city && Array.isArray(data.city)) {
      return true;
    }
    if (data.region && Array.isArray(data.region)) {
      return true;
    }
    if (data.postal_code && Array.isArray(data.postal_code)) {
      return true;
    }
    if (data.country && Array.isArray(data.country)) {
      return true;
    }
    if (data.country_code && Array.isArray(data.country_code)) {
      return true;
    }
    return false;
  }
}

export class QuoteVentilationRoomFieldError
  implements IQuoteVentilationRoomFieldError {
  quote: string[] = [];
  description: string[] = [];

  constructor(data?: IQuoteVentilationRoomFieldErrorData) {
    if (data) {
      this.quote = data.quote ?? [];
      this.description = data.description ?? [];
    }
  }

  clearErrors(): void {
    this.quote.splice(0, this.quote.length);
    this.description.splice(0, this.description.length);
  }

  static isQuoteVentilationRoomFieldError(
    data: IQuoteVentilationRoomFieldErrorData
  ): boolean {
    if (data.quote && Array.isArray(data.quote)) {
      return true;
    }
    if (data.description && Array.isArray(data.description)) {
      return true;
    }

    return false;
  }
}

export class QuoteVentilationCapacityFieldError
  implements IQuoteVentilationCapacityFieldError {
  quote: string[] = [];
  incubation_chiller_capacities_kw: string[] = [];
  ventilation_chiller_capacities_kw: string[] = [];
  incubation_boiler_capacities_kw: string[] = [];
  ventilation_boiler_capacities_kw: string[] = [];
  summer_temp_c: string[] = [];
  summer_temp_f: string[] = [];
  winter_temp_c: string[] = [];
  winter_temp_f: string[] = [];
  summer_rh: string[] = [];
  winter_rh: string[] = [];
  voltage_phase_frequency: string[] = [];
  boiler_type: string[] = [];
  ventilation_chiller_capacities_ton: string[] = [];
  incubation_chiller_capacities_ton: string[] = [];
  ventilation_boiler_capacities_mbh: string[] = [];
  incubation_boiler_capacities_mbh: string[] = [];

  constructor(data?: IQuoteVentilationCapacityFieldErrorData) {
    if (data) {
      this.quote = data.quote ?? [];
      this.incubation_chiller_capacities_kw =
        data.incubation_chiller_capacities_kw ?? [];
      this.ventilation_chiller_capacities_kw =
        data.ventilation_chiller_capacities_kw ?? [];
      this.incubation_boiler_capacities_kw =
        data.incubation_boiler_capacities_kw ?? [];
      this.ventilation_boiler_capacities_kw =
        data.ventilation_boiler_capacities_kw ?? [];
      this.summer_temp_c = data.summer_temp_c ?? [];
      this.summer_temp_f = data.summer_temp_f ?? [];
      this.winter_temp_c = data.winter_temp_c ?? [];
      this.winter_temp_f = data.winter_temp_f ?? [];
      this.summer_rh = data.summer_rh ?? [];
      this.winter_rh = data.winter_rh ?? [];
      this.voltage_phase_frequency = data.voltage_phase_frequency ?? [];
      this.boiler_type = data.boiler_type ?? [];
      this.ventilation_chiller_capacities_ton =
        data.ventilation_chiller_capacities_ton ?? [];
      this.incubation_chiller_capacities_ton =
        data.incubation_chiller_capacities_ton ?? [];
      this.ventilation_boiler_capacities_mbh =
        data.ventilation_boiler_capacities_mbh ?? [];
      this.incubation_boiler_capacities_mbh =
        data.incubation_boiler_capacities_mbh ?? [];
    }
  }

  clearErrors(): void {
    this.quote.splice(0, this.quote.length);
    this.incubation_chiller_capacities_kw.splice(
      0,
      this.incubation_chiller_capacities_kw.length
    );
    this.ventilation_chiller_capacities_kw.splice(
      0,
      this.ventilation_chiller_capacities_kw.length
    );
    this.incubation_boiler_capacities_kw.splice(
      0,
      this.incubation_boiler_capacities_kw.length
    );
    this.ventilation_boiler_capacities_kw.splice(
      0,
      this.ventilation_boiler_capacities_kw.length
    );
    this.summer_temp_c.splice(0, this.summer_temp_c.length);
    this.summer_temp_f.splice(0, this.summer_temp_f.length);
    this.winter_temp_c.splice(0, this.winter_temp_c.length);
    this.winter_temp_f.splice(0, this.winter_temp_f.length);
    this.summer_rh.splice(0, this.summer_rh.length);
    this.winter_rh.splice(0, this.winter_rh.length);
    this.voltage_phase_frequency.splice(0, this.voltage_phase_frequency.length);
    this.boiler_type.splice(0, this.boiler_type.length);
    this.ventilation_chiller_capacities_ton.splice(
      0,
      this.ventilation_chiller_capacities_ton.length
    );
    this.incubation_chiller_capacities_ton.splice(
      0,
      this.incubation_chiller_capacities_ton.length
    );
    this.ventilation_boiler_capacities_mbh.splice(
      0,
      this.ventilation_boiler_capacities_mbh.length
    );
    this.incubation_boiler_capacities_mbh.splice(
      0,
      this.incubation_boiler_capacities_mbh.length
    );
  }

  static isQuoteVentilationCapacityFieldError(
    data: IQuoteVentilationCapacityFieldErrorData
  ): boolean {
    if (data.quote && Array.isArray(data.quote)) {
      return true;
    }
    if (
      data.incubation_chiller_capacities_kw &&
      Array.isArray(data.incubation_chiller_capacities_kw)
    ) {
      return true;
    }
    if (
      data.ventilation_chiller_capacities_kw &&
      Array.isArray(data.ventilation_chiller_capacities_kw)
    ) {
      return true;
    }
    if (
      data.incubation_boiler_capacities_kw &&
      Array.isArray(data.incubation_boiler_capacities_kw)
    ) {
      return true;
    }
    if (
      data.ventilation_boiler_capacities_kw &&
      Array.isArray(data.ventilation_boiler_capacities_kw)
    ) {
      return true;
    }
    if (data.summer_temp_c && Array.isArray(data.summer_temp_c)) {
      return true;
    }
    if (data.summer_temp_f && Array.isArray(data.summer_temp_f)) {
      return true;
    }
    if (data.winter_temp_c && Array.isArray(data.winter_temp_c)) {
      return true;
    }
    if (data.winter_temp_f && Array.isArray(data.winter_temp_f)) {
      return true;
    }
    if (data.summer_rh && Array.isArray(data.summer_rh)) {
      return true;
    }
    if (data.winter_rh && Array.isArray(data.winter_rh)) {
      return true;
    }
    if (
      data.voltage_phase_frequency &&
      Array.isArray(data.voltage_phase_frequency)
    ) {
      return true;
    }
    if (data.boiler_type && Array.isArray(data.boiler_type)) {
      return true;
    }
    if (
      data.ventilation_chiller_capacities_ton &&
      Array.isArray(data.ventilation_chiller_capacities_ton)
    ) {
      return true;
    }
    if (
      data.incubation_chiller_capacities_ton &&
      Array.isArray(data.incubation_chiller_capacities_ton)
    ) {
      return true;
    }
    if (
      data.ventilation_boiler_capacities_mbh &&
      Array.isArray(data.ventilation_boiler_capacities_mbh)
    ) {
      return true;
    }
    if (
      data.incubation_boiler_capacities_mbh &&
      Array.isArray(data.incubation_boiler_capacities_mbh)
    ) {
      return true;
    }
    return false;
  }
}
